import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import Global from '../../../../../../Services/Common/Global';
import Commons from '../../../../../../Services/Common/Common';

const EscapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// 서버 상대경로 / 절대 URL / data URL / blob → img src
const ResolvePhotoSrc = (raw) => {
  const v = raw == null ? '' : String(raw).trim();
  if (!v) return '';
  const low = v.toLowerCase();
  if (
    low.startsWith('http://') ||
    low.startsWith('https://') ||
    low.startsWith('data:') ||
    low.startsWith('blob:')
  ) {
    return v;
  }

  const base = String(Global.file?.useUrl ?? '').replace(/\/?$/, '/');
  let path = v.replace(/\\/g, '/');
  if (base) {
    path = path.replace(new RegExp(`^${EscapeRegex(base)}files/`, 'i'), '');
  }
  path = path.replace(/^\/?files\//i, '');
  const enc = Commons.EncodeUrl(path);
  if (!enc) return '';
  const fileBase = Global.file?.useUrl;
  if (!fileBase) return '';
  return `${String(fileBase).replace(/\/?$/, '/')}files/${enc}`;
};

export default function PhotoReuseLayerModal({
  open,
  onClose,
  recentPhotos = [],
  currentPhotoPath = '',
  currentPhotoName = '',
  onPickRecent,
  onUploadFile,
  sideLabel = '',
}) {
  const inputRef = React.useRef(null);
  const [localError, setLocalError] = useState('');
  const [keyword, setKeyword] = useState('');

  const title = useMemo(() => {
    const s = String(sideLabel ?? '').trim();
    return s ? `사진 선택 (${s})` : '사진 선택';
  }, [sideLabel]);

  const filteredPhotos = useMemo(() => {
    const kw = String(keyword ?? '')
      .trim()
      .toLowerCase();
    if (!kw) return recentPhotos ?? [];
    return (recentPhotos ?? []).filter((p) => {
      const name = String(p?.fileName ?? p?.saveName ?? '').toLowerCase();
      return name.includes(kw);
    });
  }, [keyword, recentPhotos]);

  const handlePickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    setLocalError('');
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setLocalError('이미지 파일만 선택할 수 있습니다.');
      return;
    }
    await onUploadFile?.(file);
    onClose?.();
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: 'min(1200px, 98vw)',
          height: 'min(780px, 92vh)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" component="span" fontWeight={800}>
          {title}
        </Typography>
        <Button
          onClick={onClose}
          variant="text"
          sx={{ minWidth: 0, color: 'text.secondary' }}
          startIcon={<CloseIcon />}
        >
          닫기
        </Button>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: 'calc(100% - 64px)',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handlePickFile}
        />

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 2 }}>
          {/* 왼쪽: 현재 행(현재 칸) 미리보기 */}
          <Box
            sx={{
              width: '42%',
              minWidth: 360,
              flexShrink: 0,
              minHeight: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                p: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="subtitle2" fontWeight={800}>
                현재 선택된 이미지 미리보기
              </Typography>
              <Typography variant="caption" color="text.secondary">
                지금 수정하려는 행의 사진입니다. 오른쪽 목록에서 클릭하면 이
                칸에 바로 적용됩니다.
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {ResolvePhotoSrc(currentPhotoPath) ? (
                  <Box
                    component="img"
                    src={ResolvePhotoSrc(currentPhotoPath)}
                    alt=""
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    등록된 사진이 없습니다.
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {currentPhotoName
                  ? `파일명: ${currentPhotoName}`
                  : '파일명이 없습니다.'}
              </Typography>
            </Box>
          </Box>

          {/* 오른쪽: 업로드 이력(서버 조회) 리스트 */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                p: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={800}>
                  업로드 이력(재사용 가능)
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddAPhotoOutlinedIcon />}
                  onClick={() => inputRef.current?.click()}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    borderRadius: 1.5,
                    px: 1.4,
                    bgcolor: '#2CA900',
                    '&:hover': { bgcolor: '#217F00' },
                  }}
                >
                  내 컴퓨터에서 찾기
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary">
                이 목록은 [안전신문고/위험성평가하기]에서 등록된{' '}
                <b>이미지 목록</b>입니다. 같은 사진을 여러 공정/여러 행에서 반복
                사용하려면 여기서 클릭해서 재사용하세요.
              </Typography>
              {localError ? (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {localError}
                </Typography>
              ) : null}
              <Box sx={{ pt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="이미지명 검색 (예: 점검, 스크린, 승인...)"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </Box>
            </Box>

            <Box sx={{ p: 1.5, overflow: 'auto' }}>
              {filteredPhotos.length ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      sm: 'repeat(3, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 1.25,
                  }}
                >
                  {filteredPhotos.map((p) => {
                    const thumbSrc = ResolvePhotoSrc(
                      p?.finalFilePath ?? p?.previewFilePath
                    );
                    return (
                      <Box
                        key={String(
                          p?.saveName ?? p?.fileName ?? Math.random()
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => onPickRecent?.(p)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onPickRecent?.(p);
                          }
                        }}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box
                          sx={{
                            height: 92,
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.50',
                          }}
                        >
                          {thumbSrc ? (
                            <Box
                              component="img"
                              src={thumbSrc}
                              alt=""
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              -
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ p: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontWeight: 700,
                            }}
                          >
                            {p?.fileName ?? '사진'}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  조회된 업로드 이력이 없습니다. 상단의 ‘내 컴퓨터에서 찾기’로
                  새 사진을 업로드하거나, 근로자 의견 청취 데이터에 이미지가
                  등록되어 있는지 확인하세요.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ textTransform: 'none', fontWeight: 800 }}
        >
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
}
